import { Check } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";

/**
 * Wizard step indicator.
 * Pass `onSelect(index)` to make steps clickable for direct navigation.
 */
export default function CreateWizardSteps({
  steps,
  current = 0,
  onSelect,
  testid = "create-wizard-steps",
}) {
  const clickable = typeof onSelect === "function";

  return (
    <ol className="flex items-center gap-1 sm:gap-2 mb-5" data-testid={testid}>
      {steps.map((label, i) => {
        const done = i < current;
        const active = i === current;
        const inner = (
          <>
            <span className={cn(
              "grid h-5 w-5 place-items-center rounded-full text-[10px] shrink-0",
              active && "bg-primary-foreground/20",
              done && "bg-primary text-primary-foreground",
              !active && !done && "bg-background border border-border",
            )}>
              {done ? <Check className="h-3 w-3" weight="bold" /> : i + 1}
            </span>
            <span className="truncate hidden sm:inline">{label}</span>
          </>
        );
        const className = cn(
          "flex items-center gap-2 rounded-full px-2.5 py-1.5 text-xs font-semibold transition-colors w-full justify-center sm:justify-start",
          active && "bg-primary text-primary-foreground",
          done && "bg-primary/15 text-primary",
          !active && !done && "bg-muted text-muted-foreground",
          clickable && !active && "hover:bg-primary/20 hover:text-primary cursor-pointer",
          clickable && active && "cursor-default",
        );
        return (
          <li key={label} className="flex items-center min-w-0 flex-1">
            {clickable ? (
              <button
                type="button"
                className={className}
                onClick={() => { if (i !== current) onSelect(i); }}
                aria-current={active ? "step" : undefined}
                data-testid={`${testid}-step-${i}`}
              >
                {inner}
              </button>
            ) : (
              <div className={className}>{inner}</div>
            )}
            {i < steps.length - 1 && (
              <div className={cn("mx-1 h-px w-3 sm:w-6 shrink-0", done || active ? "bg-primary/40" : "bg-border")} />
            )}
          </li>
        );
      })}
    </ol>
  );
}
